import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { InternalAuditEntityDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { InternalAuditEntityRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  GetInternalAuditsQuery,
  Internal_Audit_Entity_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetInternalAuditsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { INTERNAL_AUDIT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  tagsWhereOnEntity,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

/**
 * Maps TRPC internal audit entities data to match the GraphQL query structure
 */
export const mapTrpcInternalAuditEntitiesToGraphQL = (
  trpcData: InternalAuditEntityRegisterResponseRow[] | undefined
): StrictMappedGraphQLSelection<GetInternalAuditsQuery> | undefined => {
  if (!trpcData) {
    return undefined;
  }

  return {
    internal_audit_entity: trpcData,
  };
};

const useInternalAuditsGraphqlVariables = (
  args: UseGetInternalAuditEntitiesRegisterArgs
) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Internal_Audit_Entity_Bool_Exp>(
    Parent_Type_Enum.InternalAuditEntity
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: INTERNAL_AUDIT_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.internalAuditFilterConditions,
  });

  return { where };
};

export type UseGetInternalAuditEntitiesRegisterArgs =
  DashboardFilterArgs<InternalAuditEntityDashboardDateField>;

export const useGetInternalAuditEntitiesRegister = createQueryHook<
  UseGetInternalAuditEntitiesRegisterArgs,
  InternalAuditEntityRegisterResponseRow[],
  GetInternalAuditsQuery
>({
  trpcQueryOptions: (
    trpc,
    { tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.internalAuditEntity.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
      })
    ),
  mapTrpcDataToGraphQL: (data) => ({
    internal_audit_entity: data,
  }),
  graphqlDocument: GetInternalAuditsDocument,
  graphqlVariables: useInternalAuditsGraphqlVariables,
});
